import React, { Component } from 'react';
import ReactDomServer from 'react-dom/server';
import {Map, Marker, TileLayer, Polygon, GeoJSON} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {observer, inject} from 'mobx-react';
import withLocationPersistence from '../hoc/WithLocationPersistence';
import centerOfMass from '@turf/center-of-mass';
import IconMarker from './IconMarker';
import { isMobileBrowser } from '../../lib/Helpers';

@inject('mapStore', 'routing')
@withLocationPersistence
@observer
class VandAMap extends Component {
  constructor(props) {
    super(props);

    this.map_ref = React.createRef();
    this.geojson_ref = React.createRef();

    this.props.mapStore.leafletRef = this.map_ref;
    this.props.mapStore.leafletLayerRef = this.geojson_ref;


    //Set up the extents, zooms and coordinate system for our map
    // this.mapExtent = [0.00000000, -5876.00000000, 7501.00000000, 0.00000000];
    this.mapMinZoom = 1;
    this.mapMaxZoom = 4;
    this.mapMaxResolution = 2;
    this.mapMinResolution = Math.pow(2, this.mapMaxZoom) * this.mapMaxResolution;    // with maxResolution at 0.5, this is 18 at maxZoom 6 and 8 at 4. with maxResolution 2, this is 64 at maxZoom 6 and 32 at maxResolution 4.

    // console.log(this.mapMinResolution)

    this.tileExtent = [0.00000000, -5876.00000000, 7501.00000000, 0.00000000];
    this.zoomSnap = 0;

    this.crs = L.extend({}, L.CRS.Simple, {
      transformation: new L.Transformation(1, -this.tileExtent[0], -1, this.tileExtent[3]),
      scale: (zoom)  => {
        return ((Math.pow(2, zoom) / this.mapMinResolution))
      },
      zoom:  (scale) => {
        return (Math.log(scale * this.mapMinResolution) / Math.LN2)
      }
    });
  }

  componentDidMount() {
    if (isMobileBrowser) {
      //commented out now, because mobile zoom works - hurrah
      // this.map_ref.current.leafletElement.touchZoom.disable();
      // this.map_ref.current.leafletElement.scrollWheelZoom.disable();

    }
  }

  zoomed(event) {
    // console.log(this.props.mapStore);
    // console.log(this.props.mapStore.zoom)
  }

  zoomedend(event) {
    this.updateStateAndLocationAttribute({currentTarget: {name: 'zoom', value: event.target._zoom}});
    // this.updateLocationAttribute({currentTarget: {name: 'zoom', value: event.target._zoom}});
  }

  moved(event) {
    const center = event.target.getCenter();
    const {lat, lng} = center;

    // we need to temporarily stop observing the latlng prop since we're limiting the draggable
    // bounds of the map (maxBoundsViscosity). When the user drags the map beyond the permitted
    // bounds, it animates back to the last valid position causing lots of state updates
    this.props.mapStore.toggleIgnoreProp('latlng');
    setTimeout(() => {
      this.props.mapStore.toggleIgnoreProp('latlng');
    }, 250);

    this.updateStateAndLocationAttribute({currentTarget: {name: 'latlng', value: `${lat},${lng}`}});
  }

  geoJsonFilter(feature) {
    // if( !this.props.mapStore.highlightedFeature ) return false;

    let typeMatch = false;
    let isGallery = false;

    if (this.props.mapStore.highlightedFeature) {
      const attrib = this.props.mapStore.highlightedFeature.attribute;
      typeMatch = (this.props.mapStore.highlightedFeature.value.indexOf(feature.properties[attrib])>-1);
    }

    if (feature.properties['type'] === 'gallery') {
      isGallery = true
    }

    return typeMatch || isGallery;
  }

  geoJsonOnEachFeature(feature, layer) {
    layer.on('click', (e) => {
      const feature = e.target.feature;
      if (feature.properties.id === null) return;
      if (feature.properties.selectable === false) return;
      this.props.mapStore.currentFeature = feature;

      this.props.mapStore.highlightId = feature.properties.id;

      const params = new URLSearchParams(this.props.routing.location.search);
      params.set('highlightId', feature.properties.id);
      this.props.routing.replace(`?${params.toString()}`);
    });
  }

  geoJsonPointToLayer(feature,latlng) {
    const logoUrl = require(`../../assets/images/icons-map/${feature.properties.type}.svg`);
    return L.marker(latlng, {
      icon: L.divIcon({
        className: `marker marker--floor-count-${feature.properties.associated_levels.length} marker--count-pos-${feature.properties.icon_count_position}`,
        html: ReactDomServer.renderToStaticMarkup(
          <React.Fragment>
            <img src={logoUrl} alt={feature.properties.type}/>
            {
              feature.properties.associated_levels.length > 0 &&
              <ul>
                {
                  feature.properties.associated_levels.map((level) => { return <li key={`${feature.properties.id}-level-${level}`}>{level}</li>})
                }
              </ul>
            }
          </React.Fragment>
        ),
        iconSize: [30,30],
        iconAnchor: [15,15]
      })
    })
  }

  geoJsonStyle(feature) {
    let style = {};

    if (feature.properties.id === this.props.mapStore.highlightId && this.props.mapStore.highlightId !== null) {
      style = {
        "color": "#ff00cd",
        "weight": 0,
        "opacity": 0,
        "fillOpacity": 0.8
      }
    } else if (feature.properties.id === this.props.mapStore.userLocationId) {
      style = {
        "color": "#fcf05b",
        "weight": 0,
        "opacity": 0,
        "fillOpacity": 0.8
      }
    } else if (feature.properties.id === this.props.mapStore.foundLocationId) {
      style = {
        "color": "#fcf05b",
        "weight": 0,
        "opacity": 0,
        "fillOpacity": 0.8
      }
    } else {
      style = {
        "color": "transparent",
        "weight": 0
      }
    }

    const event = this.props.mapStore.events.find((e) => e.rooms.map((f)=>f.id).indexOf(feature.properties.id)>-1);
    if( event ) {
      const event_styles = {
        color: event.rooms[0].colour_code,
        fillOpacity: 0.5,
        stroke: false
      }

      if( event.rooms.map((f) => f.id).indexOf(this.props.mapStore.highlightId)>-1) {
        event_styles.fillOpacity = 0.8
      }

      if (this.props.mapStore.currentItem && this.props.mapStore.currentItem.location_ids && this.props.mapStore.currentItem.location_ids.indexOf(feature.properties.id)>-1) {
        event_styles.fillOpacity = 1.0;
      }

      style = {style, ...event_styles};
    }

    return style;
  }

  render() {
    let trailMarkers;
    if (this.props.mapStore.trailStops.length > 0 ) {
      trailMarkers = this.props.mapStore.trailStops.map((stop, i) => {
        return <IconMarker key={`icon-marker-trails-${i}`} id={stop.id} classNames="marker--trail"
                           iconUrl={require(`../../assets/images/icons-map/trail_marker.svg`)}>
          {`${i + 1}`}
        </IconMarker>
      })
    }

    let locationMakers;
    if( this.props.mapStore.currentFeature && this.props.mapStore.currentFeature.hasOwnProperty('locations') && this.props.mapStore.currentFeature.locations.length ) {
      locationMakers = this.props.mapStore.trailStops.map((stop, i) => {
        return <IconMarker key={`icon-marker-trails-${i}`} id={stop.id} classNames="marker--trail"
                           iconUrl={require(`../../assets/images/icons-map/trail_marker.svg`)}>
          {`${i + 1}`}
        </IconMarker>
      })
    }

    const markers = [].concat(trailMarkers, locationMakers);
    const mapClasses = [];
    if( isMobileBrowser ) {
      mapClasses.push('is-mobile');
    }

    const map = <Map id="map" boundsOptions={this.tileExtent} className={mapClasses.join(' ')} center={this.props.mapStore.position} onZoomEnd={this.zoomedend.bind(this)} onZoom={this.zoomed.bind(this)} onMoveStart={() => { console.log('movestart') }} onMoveEnd={this.moved.bind(this)} zoom={this.props.mapStore.zoom} ref={this.map_ref} maxZoom={this.mapMaxZoom} minZoom={this.mapMinZoom} zoomSnap={this.zoomSnap}  attributionControl={false} crs={this.crs}>
      <TileLayer url={`${process.env.PUBLIC_URL}/tiles/${this.props.mapStore.floor}/{z}/{y}/{x}.png`} minZoom={this.mapMinZoom} maxZoom={this.mapMaxZoom} noWrap={true} tms={false}/>

      { this.props.mapStore.floorGeojson.has(this.props.mapStore.floor) &&
      <GeoJSON filter={this.geoJsonFilter.bind(this)} onEachFeature={this.geoJsonOnEachFeature.bind(this)} pointToLayer={this.geoJsonPointToLayer.bind(this)} key={`floor-${this.props.mapStore.floor}-${this.props.mapStore.componentKey}-${this.props.mapStore.foundLocationId}`} data={this.props.mapStore.floorGeojson.toJSON()[this.props.mapStore.floor]} ref={this.geojson_ref} style={this.geoJsonStyle.bind(this)} currentHighlightId={this.props.mapStore.highlightId} />}

      { this.props.mapStore.userLocationId &&
        <IconMarker id={this.props.mapStore.userLocationId} classNames="marker--you-are-here" iconUrl={require(`../../assets/images/icons-map/you_are_here.svg`)}/>
      }

      {markers}
    </Map>;

    window._map = map;

    return <div className={`m-map zoom-${String(this.props.mapStore.zoom).replace(/\./,'point')}`} data-georender={this.props.mapStore.geoJsonRenderTime}>
      {map}
    </div>
  }
}

export default VandAMap;
